import { useEffect } from 'react'
import type { TFunction } from 'i18next'
import type { FilterData } from './search-elements/FilterModal'
import type { NurseryItemData } from './nureseries/type'

// Modal background scroll lock hook
export const useModalScrollLock = (isOpen: boolean) => {
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto'
    // Cleanup function
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [isOpen])
}

export const dayOptions = () => {
  return Array.from({ length: 31 }, (_, i) => {
    return {
      value: String(i + 1),
      label: String(i + 1),
    }
  })
}

// Get month options (1-12) with names
export const monthOptions = (t: TFunction) => {
  const monthNames = t('month.abbr_month_names', { returnObjects: true }) as string[]
  return Array.from({ length: 12 }, (_, i) => {
    const monthNum = i + 1
    return {
      value: String(monthNum),
      label: monthNames[monthNum],
    }
  })
}

// Get year options (2015-2025 range for children birthdates)
export const yearOptions = () => {
  const currentYear = new Date().getFullYear()
  const startYear = currentYear - 13
  const years = []
  for (let year = currentYear; year >= startYear; year--) {
    years.push({
      value: String(year),
      label: String(year),
    })
  }
  return years
}

// Home menu options
export const HOME_MENU_ITEMS = [
  { id: 'service', label: 'Service Site', href: 'https://preschool-exchange.com/' },
  { id: 'destination', label: 'Destinations List', href: '/' },
  { id: 'mypage', label: 'MyPage', href: '/mypage' },
]

// Filter section type
export type FilterSection = 'mobilityConditions' | 'mealService' | 'nurseryFeatures' | 'roomType' | 'kitchen' | 'bathroom'

export const SECTION_LABELS: Record<FilterSection, string> = {
  mobilityConditions: 'Mobility',
  mealService: 'Meals',
  nurseryFeatures: 'Features',
  roomType: 'Accommodation',
  kitchen: 'Kitchen',
  bathroom: 'Bathroom',
}

// Filter Options for FilterModal
export const MOBILITY_OPTIONS = [
  { label: 'No car needed for daily life', value: '車なしで移動可能' },
  { label: 'No car needed for school commute only', value: '徒歩登園' },
]

export const MEAL_SERVICE_OPTIONS = [
  {
    label: 'Allergy-friendly Lunch (upon request)',
    value: ['Available (bring your own bento)', 'Available (exclusion meals)'],
  },
]

export const NURSERY_FEATURE_OPTIONS = [
  { label: 'Top Picks', value: 'Top Picks' },
  { label: 'Sea', value: '海遊び' },
  { label: 'Forest', value: '森遊び' },
  { label: 'Snow', value: '雪遊び' },
  { label: 'Heritage', value: 'Heritage' },
  { label: 'Elementary OK', value: 'Elementary OK' },
]

export const ROOM_TYPE_OPTIONS = [
  { label: 'Serviced Apartment', value: 'Aparthotel' },
  { label: 'Guesthouse', value: 'Guesthouse' },
  { label: 'Hotel', value: 'Hotel' },
  { label: 'JPE House', value: 'JPE House' },
  { label: 'Private House', value: 'Private home' },
  { label: 'Ryokan (Japanese Inn)', value: 'Ryokan (Japanese inn)' },
  { label: 'Entire Home Rental', value: 'Whole building rental' },
]

export const KITCHEN_OPTIONS = [{ label: 'Private Kitchen', value: 'Private Kitchen' }]

export const BATHROOM_OPTIONS = [{ label: 'Private Bathroom', value: ['Bathtub Available', 'Shower Booth Only'] }]

// Helper function to get filter label by section and value
export const getFilterLabel = (section: FilterSection, value: string): string | undefined => {
  const optionsMap: Record<FilterSection, Array<{ label: string; value: string | string[] }>> = {
    mobilityConditions: MOBILITY_OPTIONS,
    mealService: MEAL_SERVICE_OPTIONS,
    nurseryFeatures: NURSERY_FEATURE_OPTIONS,
    roomType: ROOM_TYPE_OPTIONS,
    kitchen: KITCHEN_OPTIONS,
    bathroom: BATHROOM_OPTIONS,
  }

  const label = optionsMap[section]?.find((option) => {
    if (Array.isArray(option.value)) {
      return option.value.includes(value)
    }
    return option.value === value
  })?.label
  return label
}

interface CurrencyRate {
  code: string
  symbol: string
  rate: number
}

// Currency options for currency selector
const currencyRateInput = document.getElementById('currency_exchange_rates') as HTMLInputElement
export const CURRENCIES: CurrencyRate[] = currencyRateInput ? JSON.parse(currencyRateInput.value) : []

export const convertPrice = (amountInJPY: number, targetCurrency: string): number => {
  const rate = CURRENCIES.find((currency) => currency.code === targetCurrency)?.rate
  return amountInJPY * (rate ?? 1)
}

const formatNumber = (amount: number): string => {
  const roundedAmount = Math.round(amount)
  return roundedAmount.toLocaleString('en-US')
}

const CURRENCY_SYMBOL_MAP: Record<string, string> = {
  JPY: '¥',
  USD: '$',
  SGD: 'S$',
  CAD: 'C$',
  TWD: 'NT$',
  HKD: 'HK$',
  AUD: 'A$',
  GBP: '£',
  CNY: '¥',
  THB: '฿',
}

export const formatPrice = (amountInJPY: number, currency: string): string => {
  const convertedAmount = convertPrice(amountInJPY, currency)
  const symbol = CURRENCIES.find((c) => c.code === currency)?.symbol || CURRENCY_SYMBOL_MAP[currency] || currency
  const formattedNumber = formatNumber(convertedAmount)

  return `${symbol}${formattedNumber}`
}

// For the first line: converts JPY to selected currency with proper symbol
export const formatConvertedPrice = (amountInJPY: number, currency: string): string => {
  const convertedAmount = convertPrice(amountInJPY, currency)
  const symbol = CURRENCY_SYMBOL_MAP[currency] || CURRENCIES.find((c) => c.code === currency)?.symbol || currency
  const formattedNumber = formatNumber(convertedAmount)

  return `${symbol}${formattedNumber}`
}

// For the second line: always displays the original JPY amount with yen symbol
export const formatJPYPrice = (amountInJPY: number): string => {
  const formattedNumber = formatNumber(amountInJPY)
  return `¥${formattedNumber}`
}

export const parsePriceString = (priceString: string): number | null => {
  const numericString = priceString.replace(/[¥$€£,\s]/g, '')
  const parsed = parseFloat(numericString)
  return isNaN(parsed) ? null : parsed
}

// Helper function to get nearest 3 months
export const handleGetTheNearestMonth = (): string[] => {
  const today = new Date()
  const year = today.getFullYear()
  const month = today.getMonth() + 1
  const result: string[] = []

  for (let i = 0; i < 3; i++) {
    const currentYear = year + Math.floor((month + i - 1) / 12)
    const currentMonth = ((month + i - 1) % 12) + 1
    result.push(`${currentYear}/${currentMonth.toString().padStart(2, '0')}`)
  }

  return result
}

// Add a param if numeric value > 0
export const addNumericParam = (params: Record<string, string>, key: string, value: number | undefined): void => {
  if (value && value > 0) {
    params[key] = String(value)
  }
}

// Add a param if array has items (joins with comma)
export const addArrayParam = (
  params: Record<string, string>,
  key: string,
  array: any[] | undefined,
  transformer?: (item: any) => string,
): void => {
  if (array && array.length > 0) {
    const values = transformer ? array.map(transformer) : array
    params[key] = values.join(',')
  }
}

// Add a param if value exists (truthy check)
export const addParamIfExists = (params: Record<string, string>, key: string, value: string | undefined): void => {
  if (value) {
    params[key] = value
  }
}

// Helper function to build filter query params
export const buildFilterQueryParams = (filterData: FilterData | null, currentQueryParams: URLSearchParams) => {
  if (!filterData) {
    return
  }

  try {
    const filterMap = [
      {
        key: 'room_environment',
        value: filterData.mobilityConditions,
      },
      {
        key: 'allergies_en',
        value: filterData.mealService,
      },
      {
        key: 'feature',
        value: filterData.nurseryFeatures,
      },
      {
        key: 'room_type_en',
        value: filterData.roomType,
      },
      {
        key: 'kitchen_en',
        value: filterData.kitchen,
      },
      {
        key: 'bathroom_en',
        value: filterData.bathroom,
      },
    ]

    filterMap.forEach(({ key, value }) => {
      if (value && value.length > 0) {
        currentQueryParams.set(key, value.join(','))
      } else {
        currentQueryParams.delete(key)
      }
    })

    // Search Text
    if (filterData.searchText && filterData.searchText.trim()) {
      currentQueryParams.set('text_search', filterData.searchText)
    } else {
      currentQueryParams.delete('text_search')
    }
  } catch (error) {
    console.error('Error in buildFilterQueryParams:', error, 'filterData:', filterData)
    throw error
  }
}

export const nurseryNameWithPrefecture = (nursery: NurseryItemData): string => {
  const prefectureList = JSON.parse((document.getElementById('prefectures_list') as HTMLInputElement)?.value) || []
  const prefecture = prefectureList.find((item: any) => item.text === nursery.prefecture)?.text_en || ''
  return `${prefecture ? `${prefecture} - ` : ''} ${nursery.alias}`
}

// Copy text to clipboard with fallback for environments without clipboard API
export const copyToClipboard = async (text: string): Promise<void> => {
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(text)
  } else {
    // Fallback for environments without clipboard API
    const textArea = document.createElement('textarea')
    textArea.value = text
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
  }
}
